import { DateTime } from 'luxon';

export interface DateRangeOptions {
  raw: string | null;
  min?: string;
  max?: string;
  mode: 'date' | 'datetime';
}

export function validateDateRange({ raw, min, max, mode }: DateRangeOptions) {
  if (!raw || !min || !max) return null;

  const date = DateTime.fromISO(raw);
  const minDate = DateTime.fromISO(min);
  const maxDate = DateTime.fromISO(max);

  if (mode === 'date') {
    if (date.startOf('day') < minDate.startOf('day') || date.startOf('day') > maxDate.startOf('day')) {
      return { invalid: true };
    }
    return null;
  }

  if (date < minDate || date > maxDate) {
    return { invalid: true };
  }

  return null;
}

export function computePayWeekRange(payWeekEnd?: string) {
  if (!payWeekEnd) return {};

  const endOfWeek = DateTime.fromISO(payWeekEnd);
  const startOfWeek = endOfWeek.minus({ days: 6 });

  return {
    minDate: startOfWeek.toFormat('yyyy-MM-dd'),
    maxDate: endOfWeek.toFormat('yyyy-MM-dd'),

    minDateTime: startOfWeek.startOf('day').toFormat("yyyy-MM-dd'T'HH:mm"),
    maxDateTime: endOfWeek.endOf('day').toFormat("yyyy-MM-dd'T'HH:mm"),
  };
}

/**
 * Shifts a `yyyy-MM-dd` date by whole calendar days and returns `yyyy-MM-dd`.
 *
 * Pure calendar arithmetic: unlike `new Date(iso)` + `setDate` + `toISOString()`, this does not
 * mix UTC parsing with local-time arithmetic, which shifts dates by a day across DST transitions
 * (e.g. the week ending 2026-11-07 in America/Chicago produced 2026-10-31 for Sunday 2026-11-01).
 */
export function addDaysToIsoDate(isoDate: string, days: number): string {
  return DateTime.fromISO(isoDate, { zone: 'utc' }).plus({ days }).toISODate()!;
}

/**
 * Returns the `yyyy-MM-dd` date of a day within the work week ending on `workWeekEndDate`.
 * `day` is 1-based: 1 = first day of the week (end date - 6), 7 = the end date.
 */
export function dateOfDayInWorkWeek(workWeekEndDate: string, day: number): string {
  return addDaysToIsoDate(workWeekEndDate, day - 7);
}

export function formatDayWithDateLabel(endDate: string, i: number) {
  if (!endDate) return '';
  const dateTime = DateTime.fromISO(endDate).minus({ days: 6 }).plus({ days: i });
  return `${dateTime.toFormat('EEEE')} (${dateTime.toFormat('MMM dd')})`;
}

export function getTargetEndDate(): string {
  const now = DateTime.now();
  const weekday = now.weekday; // 1=Mon, 7=Sun

  let targetSunday: DateTime;
  if (weekday === 1) {
    // Mon: prior week's Sunday.
    targetSunday = now.startOf('week').minus({ days: 8 });
  } else {
    // Tue-Sun: previous Sunday.
    targetSunday = now.startOf('week').minus({ days: 1 });
  }
  return targetSunday.toISODate()!;
}

export function getTargetWorkWeekEndDate(): string {
  const targetSunday = DateTime.fromISO(getTargetEndDate());
  return targetSunday.minus({ days: 1 }).toISODate()!;
}
